import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { ArrowLeft, MapPin, Clock, Users, Calendar, Share2, Copy, MessageCircle } from "lucide-react";

function BookingForm({ event, onDone }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    total_participants: 1, adults: 1, children: 0, children_ages: "",
    notes: "", privacy_consent: false, newsletter_consent: false,
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) return toast.error("Devi accettare l'informativa privacy");
    setLoading(true);
    try {
      const { data } = await api.post(`/events/${event.id}/book`, form);
      toast.success(data.status === "waitlist" ? "Sei in lista d'attesa" : "Prenotazione confermata! Ti abbiamo inviato un'email");
      onDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore prenotazione");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full rounded-xl bg-white border border-[#292522]/12 px-4 py-3 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className="text-xs font-semibold text-[#292522]/70">Nome *</span>
          <input required className={input + " mt-1"} value={form.first_name} onChange={(e) => update("first_name", e.target.value)} data-testid="booking-first-name" />
        </label>
        <label className="block"><span className="text-xs font-semibold text-[#292522]/70">Cognome *</span>
          <input required className={input + " mt-1"} value={form.last_name} onChange={(e) => update("last_name", e.target.value)} data-testid="booking-last-name" />
        </label>
        <label className="block"><span className="text-xs font-semibold text-[#292522]/70">Email *</span>
          <input required type="email" className={input + " mt-1"} value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="booking-email" />
        </label>
        <label className="block"><span className="text-xs font-semibold text-[#292522]/70">Telefono</span>
          <input className={input + " mt-1"} value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="booking-phone" />
        </label>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <label><span className="text-xs font-semibold text-[#292522]/70">Partecipanti *</span>
          <input required type="number" min="1" className={input + " mt-1"} value={form.total_participants} onChange={(e) => update("total_participants", parseInt(e.target.value || "1"))} data-testid="booking-total" />
        </label>
        <label><span className="text-xs font-semibold text-[#292522]/70">Adulti</span>
          <input type="number" min="0" className={input + " mt-1"} value={form.adults} onChange={(e) => update("adults", parseInt(e.target.value || "0"))} data-testid="booking-adults" />
        </label>
        <label><span className="text-xs font-semibold text-[#292522]/70">Bambini</span>
          <input type="number" min="0" className={input + " mt-1"} value={form.children} onChange={(e) => update("children", parseInt(e.target.value || "0"))} data-testid="booking-children" />
        </label>
      </div>
      {form.children > 0 && (
        <label className="block"><span className="text-xs font-semibold text-[#292522]/70">Età bambine/i</span>
          <input className={input + " mt-1"} placeholder="es. 3, 6, 8" value={form.children_ages} onChange={(e) => update("children_ages", e.target.value)} />
        </label>
      )}
      <label className="block"><span className="text-xs font-semibold text-[#292522]/70">Note o esigenze</span>
        <textarea rows="3" className={input + " mt-1 resize-none"} value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="booking-notes" />
      </label>

      <div className="space-y-3 pt-2 border-t border-[#292522]/8">
        <label className="flex gap-3 items-start cursor-pointer">
          <input type="checkbox" required checked={form.privacy_consent} onChange={(e) => update("privacy_consent", e.target.checked)} className="mt-1 accent-[#FF6100]" data-testid="booking-privacy" />
          <span className="text-xs text-[#292522]/75">Ho letto e accetto l'<Link to="/privacy" className="text-[#FF6100] underline">Informativa privacy</Link> *</span>
        </label>
        <label className="flex gap-3 items-start cursor-pointer">
          <input type="checkbox" checked={form.newsletter_consent} onChange={(e) => update("newsletter_consent", e.target.checked)} className="mt-1 accent-[#FF6100]" data-testid="booking-newsletter" />
          <span className="text-xs text-[#292522]/75">Voglio ricevere aggiornamenti e newsletter da Vola Vola (facoltativo)</span>
        </label>
      </div>

      <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="booking-submit">
        {loading ? "Invio…" : "Prenota il tuo posto"}
      </button>
    </form>
  );
}

export default function EventoDetail() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get(`/events/slug/${slug}`).then((r) => setEv(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="container-vv py-24 text-center"><p>Evento non trovato.</p><Link to="/eventi" className="btn-primary mt-4 inline-flex">Torna agli eventi</Link></div>;
  if (!ev) return <div className="container-vv py-24 text-center text-[#292522]/50">Caricamento…</div>;

  const cover = ev.cover_image ? fileUrl(ev.cover_image) : null;
  const canBook = ["pubblicato", "iscrizioni_aperte"].includes(ev.status);
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(ev.title + " — " + currentUrl)}`, "_blank");
  const copyLink = () => { navigator.clipboard.writeText(currentUrl); toast.success("Link copiato"); };
  const addToCalendar = () => {
    const start = new Date(ev.start_date);
    const end = ev.end_date ? new Date(ev.end_date) : new Date(start.getTime() + 2 * 3600 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:${ev.title}\nDTSTART:${fmt(start)}\nDTEND:${fmt(end)}\nLOCATION:${ev.location || ""}\nDESCRIPTION:${ev.short_desc || ""}\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${ev.slug}.ics`; a.click();
  };

  return (
    <div data-testid="evento-detail-page">
      <section className="bg-[#FFF1E8] py-14">
        <div className="container-vv">
          <Link to="/eventi" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF6100] mb-6"><ArrowLeft className="h-4 w-4" /> Tutti gli eventi</Link>
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-block rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#216B65]">{ev.area || "evento"}</span>
                <span className="inline-block rounded-full bg-[#FF6100] text-white px-3 py-1 text-xs font-semibold">{ev.status?.replace("_", " ")}</span>
              </div>
              <h1 className="mt-4 font-display font-medium text-4xl sm:text-5xl tracking-tight text-[#292522] leading-[1.1]">{ev.title}</h1>
              {ev.subtitle && <p className="mt-3 text-xl text-[#292522]/70 italic">{ev.subtitle}</p>}
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <span className="inline-flex items-center gap-2 text-[#292522]/80"><Clock className="h-4 w-4 text-[#FF6100]" strokeWidth={1.75} />{formatDate(ev.start_date)}</span>
                {ev.location && <span className="inline-flex items-center gap-2 text-[#292522]/80"><MapPin className="h-4 w-4 text-[#FF6100]" strokeWidth={1.75} />{ev.location}</span>}
                {ev.capacity > 0 && <span className="inline-flex items-center gap-2 text-[#292522]/80"><Users className="h-4 w-4 text-[#FF6100]" strokeWidth={1.75} />{ev.seats_available != null ? `${ev.seats_available} posti liberi su ${ev.capacity}` : `${ev.capacity} posti`}</span>}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                <button onClick={addToCalendar} className="btn-ghost text-xs" data-testid="add-to-calendar"><Calendar className="h-4 w-4" strokeWidth={1.75} />Aggiungi al calendario</button>
                <button onClick={shareWhatsApp} className="btn-ghost text-xs" data-testid="share-whatsapp"><MessageCircle className="h-4 w-4" strokeWidth={1.75} />WhatsApp</button>
                <button onClick={copyLink} className="btn-ghost text-xs" data-testid="copy-link"><Copy className="h-4 w-4" strokeWidth={1.75} />Copia link</button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(41,37,34,0.12)] bg-white">
                {cover ? <img src={cover} alt={ev.title} className="w-full h-full object-cover" /> : (
                  <div className="w-full h-full flex items-center justify-center bg-[#FFF1E8]">
                    <img src="/logo-volavola.webp" alt="Vola Vola APS" className="w-2/3 max-w-[280px] object-contain opacity-90" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-vv py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-8">
          {ev.short_desc && (
            <div>
              <h2 className="font-display text-3xl font-medium">Di cosa si tratta</h2>
              <p className="mt-3 text-lg text-[#292522]/80 whitespace-pre-line leading-relaxed">{ev.short_desc}</p>
            </div>
          )}
          {ev.full_desc && (
            <div>
              <h3 className="font-display text-2xl font-medium">Dettagli</h3>
              <p className="mt-3 text-[#292522]/80 whitespace-pre-line leading-relaxed">{ev.full_desc}</p>
            </div>
          )}
          {ev.program && (
            <div>
              <h3 className="font-display text-2xl font-medium">Programma</h3>
              <div className="mt-3 rounded-2xl bg-[#F8F1E8] p-6 text-[#292522]/80 whitespace-pre-line">{ev.program}</div>
            </div>
          )}
          {ev.audience && (<div><h3 className="font-display text-2xl font-medium">Per chi</h3><p className="mt-2 text-[#292522]/80">{ev.audience}</p></div>)}
          {ev.accessibility && (<div><h3 className="font-display text-2xl font-medium">Accessibilità</h3><p className="mt-2 text-[#292522]/80">{ev.accessibility}</p></div>)}
        </div>

        <aside className="lg:col-span-5">
          <div className="card-vv p-8 bg-[#F8F1E8] sticky top-28">
            {confirmed ? (
              <div className="text-center py-6" data-testid="booking-confirmation">
                <div className="mx-auto h-16 w-16 rounded-full bg-[#FF6100] flex items-center justify-center text-white text-2xl">✓</div>
                <h3 className="mt-4 font-display text-2xl font-medium">{confirmed.status === "waitlist" ? "In lista d'attesa" : "Prenotazione confermata"}</h3>
                <p className="mt-2 text-sm text-[#292522]/70">Ti abbiamo mandato un'email con tutti i dettagli. A presto!</p>
              </div>
            ) : canBook ? (
              <>
                <h3 className="font-display text-2xl font-medium mb-1">Prenota il tuo posto</h3>
                <p className="text-sm text-[#292522]/60 mb-5">{ev.is_free ? "Gratuito" : `€${ev.price || 0}`}</p>
                <BookingForm event={ev} onDone={setConfirmed} />
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-[#292522]/70">{ev.status === "completo" ? "Posti esauriti" : ev.status === "annullato" ? "Evento annullato" : "Prenotazioni non disponibili"}</p>
              </div>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}
